// PKT-0913 · "Needs a touch" — who in the pipeline is going cold, ranked.
//
// WHY: the 2026-09-30 money-truth pass (AGENT-0332, §3) found 9 of 13 clients
// moving in within 45 days untouched for 7+ days — one moving in that day —
// and 90 of 107 pipeline clients untouched for 14+ days. The Pipeline board
// colours each card's last-contacted caption, but nothing RANKS who needs a
// touch first. This is that ranking. Pure, no I/O: ClientList fetches, this
// arranges (same contract as locatorWorkload.ts).
//
// DEFINITIONS (kept identical to what the Pipeline cards already show):
//   pipeline   status new_client | touring | applied. payment_received is a
//              finished deal; closed / out_of_contact are not work. Test and
//              synthetic clients are excluded, as in the truth page.
//   last touch clients.last_contacted_at — the SAME field the Pipeline card
//              caption colours grey/amber/red. It is bumped server-side by
//              migration 031's activity trigger. Known gap, reported in the
//              PR: an outbound text in client_sms_messages does NOT bump it,
//              so a client texted from Messages can still read "never" here.
//   move-in    clients.move_in_date, falling back to move_in_timeline when it
//              parses as a date (clientMoveIn + parseLeadMoveInDate), read as
//              a CALENDAR day, never a UTC instant.
//
// RANK:
//   needsTouch  last touch >= 7 days ago, or never — OR (Director Ch108
//               rule) move-in within the next 7 days or passed within 14
//               days, and last touch >= 2 days ago, or never.
//     tier 1    move-in on or after (today - 14d): soonest first. A move-in
//               that passed within 14 days is overdue and ranks ahead of today
//               (the truth page's near-move-in window starts at today - 14).
//     tier 2    no usable move-in, or one lapsed more than 14 days ago:
//               never-touched first, then most days since the last touch.
//     ties      never before touched, then most days since the last touch.
//   recent      touched within 7 days: shown below, stalest first.
//
// READ-ONLY: this module decides an order. It sends nothing and changes no
// status.

import type { Client, ClientStatus } from "../components/clients/types";
import { clientMoveIn } from "./clientMoveIn";
import { parseLeadMoveInDate } from "./leadsTimeline";

export const NEEDS_TOUCH_AFTER_DAYS = 7;
export const MOVE_IN_GRACE_DAYS = 14;
/** Director Ch108: a move-in this close lowers the touch cut to 2 days. */
export const IMMINENT_MOVE_IN_DAYS = 7;
export const IMMINENT_TOUCH_AFTER_DAYS = 2;
export const NEEDS_TOUCH_STATUSES: readonly ClientStatus[] = ["new_client", "touring", "applied"];

const DAY_MS = 86_400_000;

export interface NeedsTouchRow {
  client: Client;
  agentName: string;
  /** Whole days since the recorded contact; null means missing or unreliable evidence. */
  daysSinceTouch: number | null;
  contactEvidence: "recorded" | "missing" | "invalid" | "future";
  reviewReason: string;
  moveIn: Date | null;
  /** What the client record says about move-in, verbatim, for display. */
  moveInText: string | null;
  /** Calendar days from today to move-in; negative = passed. */
  daysUntilMoveIn: number | null;
  /** Move-in passed more than MOVE_IN_GRACE_DAYS ago: ranked as undated. */
  moveInLapsed: boolean;
}

export interface NeedsTouchQueue {
  needsTouch: NeedsTouchRow[];
  recent: NeedsTouchRow[];
}

function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t) || t > now.getTime()) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

function calendarDaysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / DAY_MS);
}

function toRow(client: Client, now: Date): NeedsTouchRow {
  const moveInText = clientMoveIn(client) ?? null;
  const moveIn = parseLeadMoveInDate(moveInText);
  const daysUntilMoveIn = moveIn ? calendarDaysBetween(now, moveIn) : null;
  const agent = client.assigned_agent;
  const contactTime = client.last_contacted_at ? Date.parse(client.last_contacted_at) : NaN;
  const contactEvidence = !client.last_contacted_at ? "missing"
    : Number.isNaN(contactTime) ? "invalid"
    : contactTime > now.getTime() ? "future" : "recorded";
  const imminent = daysUntilMoveIn !== null && daysUntilMoveIn >= -MOVE_IN_GRACE_DAYS
    && daysUntilMoveIn <= IMMINENT_MOVE_IN_DAYS;
  const age = daysSince(client.last_contacted_at, now);
  const reviewReason = contactEvidence !== "recorded"
    ? `${contactEvidence === "missing" ? "Missing" : contactEvidence === "future" ? "Future" : "Invalid"} contact date — verify history`
    : age! >= (imminent ? IMMINENT_TOUCH_AFTER_DAYS : NEEDS_TOUCH_AFTER_DAYS)
      ? `Recorded contact ${age}d ago${imminent ? `; near move-in (${IMMINENT_TOUCH_AFTER_DAYS}d threshold)` : `; ${NEEDS_TOUCH_AFTER_DAYS}d threshold`}`
      : "Recent recorded contact";
  return {
    client,
    contactEvidence,
    reviewReason,
    agentName: client.assigned_agent_id ? agent?.full_name || agent?.email || "Locator" : "Unassigned",
    daysSinceTouch: age,
    moveIn,
    moveInText,
    daysUntilMoveIn,
    moveInLapsed: daysUntilMoveIn !== null && daysUntilMoveIn < -MOVE_IN_GRACE_DAYS,
  };
}

/** Never-touched first, then the most days since the last touch. */
function byStalest(a: NeedsTouchRow, b: NeedsTouchRow): number {
  const da = a.daysSinceTouch === null ? Number.POSITIVE_INFINITY : a.daysSinceTouch;
  const db = b.daysSinceTouch === null ? Number.POSITIVE_INFINITY : b.daysSinceTouch;
  if (da !== db) return db - da;
  return a.client.id.localeCompare(b.client.id);
}

function hasRankableMoveIn(r: NeedsTouchRow): boolean {
  return r.daysUntilMoveIn !== null && !r.moveInLapsed;
}

function isImminent(r: NeedsTouchRow): boolean {
  return r.daysUntilMoveIn !== null && r.daysUntilMoveIn <= IMMINENT_MOVE_IN_DAYS && !r.moveInLapsed;
}

function isDue(r: NeedsTouchRow): boolean {
  if (r.daysSinceTouch === null) return true;
  const cut = isImminent(r) ? IMMINENT_TOUCH_AFTER_DAYS : NEEDS_TOUCH_AFTER_DAYS;
  return r.daysSinceTouch >= cut;
}

export function buildNeedsTouchQueue(clients: Client[], now: Date = new Date()): NeedsTouchQueue {
  const rows = clients
    .filter((c) => NEEDS_TOUCH_STATUSES.includes(c.status))
    .filter((c) => c.is_test_client !== true && c.is_synthetic !== true)
    .map((c) => toRow(c, now));

  const needsTouch = rows.filter(isDue);
  const recent = rows.filter((r) => !isDue(r));

  needsTouch.sort((a, b) => {
    const ra = hasRankableMoveIn(a);
    const rb = hasRankableMoveIn(b);
    if (ra !== rb) return ra ? -1 : 1;
    if (ra && rb && a.daysUntilMoveIn !== b.daysUntilMoveIn) {
      return (a.daysUntilMoveIn as number) - (b.daysUntilMoveIn as number);
    }
    return byStalest(a, b);
  });
  recent.sort(byStalest);

  return { needsTouch, recent };
}
